import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertTriangle, CheckCircle2, XCircle, Loader2, GitPullRequest,
  FlaskConical, ChevronUp, Package, ArrowUpRight, Shield,
} from 'lucide-react'
import { guardianService, type GuardianFinding, type GuardianQARun } from '@/services/guardian'
import { GuardianReleaseStatus } from './guardian-release-status'

const severityConfig: Record<string, { label: string; color: string; bg: string; order: number }> = {
  critical: { label: 'Critical', color: 'text-red-700', bg: 'bg-red-100', order: 0 },
  high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-100', order: 1 },
  medium: { label: 'Medium', color: 'text-amber-700', bg: 'bg-amber-100', order: 2 },
  low: { label: 'Low', color: 'text-blue-700', bg: 'bg-blue-100', order: 3 },
  info: { label: 'Info', color: 'text-slate-600', bg: 'bg-slate-100', order: 4 },
}

const typeLabels: Record<string, string> = {
  outdated: 'Outdated',
  vulnerability: 'CVE',
  deprecation: 'Deprecated',
  drift: 'Version Drift',
}

interface Props {
  appSlug: string
  appName: string
  onClose: () => void
}

type WorkflowStep = 'select' | 'qa-running' | 'qa-done' | 'pr-creating' | 'pr-done'

export function GuardianAppDetail({ appSlug, appName, onClose }: Props) {
  const [findings, setFindings] = useState<GuardianFinding[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [step, setStep] = useState<WorkflowStep>('select')
  const [qaTaskId, setQaTaskId] = useState<string | null>(null)
  const [qaRun, setQaRun] = useState<GuardianQARun | null>(null)
  const [qaChecks, setQaChecks] = useState<any[]>([])
  const [prTaskId, setPrTaskId] = useState<string | null>(null)
  const [prUrl, setPrUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadFindings()
  }, [appSlug])

  async function loadFindings() {
    setLoading(true)
    try {
      const data = await guardianService.getAppFindings(appSlug)
      // Sort by severity, keep all non-resolved/dismissed
      const actionable = data.filter((f: GuardianFinding) =>
        f.finding_status !== 'resolved' && f.finding_status !== 'dismissed'
      )
      actionable.sort((a: GuardianFinding, b: GuardianFinding) => {
        // Show open findings first, then in_progress
        const statusOrder = (s: string) => s === 'in_progress' ? 1 : 0
        const sd = statusOrder(a.finding_status) - statusOrder(b.finding_status)
        if (sd !== 0) return sd
        const aOrder = severityConfig[a.severity]?.order ?? 5
        const bOrder = severityConfig[b.severity]?.order ?? 5
        return aOrder - bOrder
      })
      setFindings(actionable)
    } catch (e) {
      console.error('Failed to load findings:', e)
    } finally {
      setLoading(false)
    }
  }

  function toggleFinding(id: number) {
    if (step !== 'select') return
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const selectableFindings = findings.filter(f => !(f.finding_status === 'in_progress' && f.fix_pr_url))

  function selectAll() {
    if (selected.size === selectableFindings.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(selectableFindings.map(f => f.id)))
    }
  }

  const pollQAStatus = useCallback(async () => {
    try {
      const runs = await guardianService.getQARuns(appSlug)
      // Find the most recent run
      if (runs.length > 0) {
        const latest = runs[0]
        if (latest.run_status === 'completed' || latest.run_status === 'failed') {
          setQaRun(latest)
          const checks = await guardianService.getQAChecks(latest.id)
          setQaChecks(checks)
          setStep(latest.overall_verdict === 'pass' ? 'qa-done' : 'qa-done')
          return true // done polling
        }
      }
    } catch {}
    return false // keep polling
  }, [appSlug])

  async function handleRunQA() {
    if (selected.size === 0) return
    setError(null)
    setStep('qa-running')
    try {
      const result = await guardianService.triggerBundleQA(appSlug, Array.from(selected))
      setQaTaskId(result.task_id)

      // Poll for completion
      const poll = setInterval(async () => {
        const done = await pollQAStatus()
        if (done) clearInterval(poll)
      }, 3000)

      // Safety timeout after 5 minutes
      setTimeout(() => clearInterval(poll), 300000)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to trigger QA')
      setStep('select')
    }
  }

  async function handleCreatePR() {
    if (!qaRun || selected.size === 0) return
    setError(null)
    setStep('pr-creating')
    try {
      const result = await guardianService.createBundledPR(appSlug, Array.from(selected), qaRun.id)
      setPrTaskId(result.task_id)

      // Poll for PR completion (action record with pr_url)
      const poll = setInterval(async () => {
        try {
          const actions = await guardianService.getAppActions(appSlug)
          const completed = actions.find((a: any) => a.action_status === 'completed' && a.result)
          if (completed) {
            clearInterval(poll)
            const resultData = typeof completed.result === 'string' ? JSON.parse(completed.result) : completed.result
            if (resultData?.pr_url) setPrUrl(resultData.pr_url)
            setStep('pr-done')
          }
        } catch {}
      }, 2000)

      // Safety timeout
      setTimeout(() => { clearInterval(poll); if (step === 'pr-creating') setStep('pr-done') }, 60000)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to create PR')
      setStep('qa-done')
    }
  }

  function handleReset() {
    setStep('select')
    setQaRun(null)
    setQaChecks([])
    setQaTaskId(null)
    setPrTaskId(null)
    setPrUrl(null)
    setError(null)
    loadFindings()
  }

  const allSelected = selectableFindings.length > 0 && selected.size === selectableFindings.length

  return (
    <div className="bg-slate-50 border-t border-b">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b">
        <div className="flex items-center gap-3">
          <Shield className="h-4 w-4 text-blue-600" />
          <span className="font-semibold text-slate-900">{appName}</span>
          <Badge variant="outline" className="text-xs">
            {findings.length} finding{findings.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="gap-1 text-slate-500">
          <ChevronUp className="h-4 w-4" />
          Close
        </Button>
      </div>

      <div className="p-6 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading findings...
          </div>
        ) : findings.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-600">No actionable findings for this app.</p>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {step === 'select' && selectableFindings.length > 0 && (
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
                {selected.size > 0 && step === 'select' && (
                  <span className="text-sm text-slate-500">{selected.size} selected</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {step === 'select' && selected.size > 0 && (
                  <Button onClick={handleRunQA} className="gap-2">
                    <FlaskConical className="h-4 w-4" />
                    Validate {selected.size} Update{selected.size > 1 ? 's' : ''}
                  </Button>
                )}

                {step === 'qa-running' && (
                  <Button disabled className="gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running QA...
                  </Button>
                )}

                {step === 'qa-done' && qaRun?.overall_verdict === 'pass' && (
                  <Button onClick={handleCreatePR} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
                    <GitPullRequest className="h-4 w-4" />
                    Create PR with {selected.size} Update{selected.size > 1 ? 's' : ''}
                  </Button>
                )}

                {step === 'qa-done' && qaRun?.overall_verdict !== 'pass' && (
                  <Button variant="outline" onClick={handleReset} className="gap-2">
                    Try Again
                  </Button>
                )}

                {step === 'pr-creating' && (
                  <Button disabled className="gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating PR...
                  </Button>
                )}

                {step === 'pr-done' && (
                  <Button variant="outline" onClick={handleReset} className="gap-2 text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" />
                    PR Created
                  </Button>
                )}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <XCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            {/* QA Results Banner */}
            {step === 'qa-running' && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                  <FlaskConical className="h-4 w-4" />
                  Validating {selected.size} updates in sandbox...
                </div>
                <div className="h-1.5 bg-blue-200 rounded-full overflow-hidden">
                  <div className="h-full w-1/3 bg-blue-500 rounded-full animate-pulse" />
                </div>
                <p className="text-xs text-blue-600">Installing dependencies, running type checks, lint, tests, and build.</p>
              </div>
            )}

            {step === 'qa-done' && qaRun && (
              <div className={`p-4 rounded-lg border space-y-3 ${
                qaRun.overall_verdict === 'pass'
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2 text-sm font-medium">
                  {qaRun.overall_verdict === 'pass' ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <span className="text-emerald-800">All QA checks passed — safe to create PR</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span className="text-red-800">QA validation failed — review issues below</span>
                    </>
                  )}
                </div>
                {qaChecks.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {qaChecks.map((check: any) => (
                      <div key={check.id} className="flex items-center gap-2 text-xs">
                        {check.check_status === 'pass' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : check.check_status === 'fail' ? (
                          <XCircle className="h-3.5 w-3.5 text-red-500" />
                        ) : (
                          <div className="h-3.5 w-3.5 rounded-full bg-slate-300" />
                        )}
                        <span className="text-slate-700 capitalize">{check.check_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 'pr-done' && (
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                  <GitPullRequest className="h-4 w-4" />
                  Pull request created successfully with {selected.size} dependency update{selected.size > 1 ? 's' : ''}.
                </div>
                {prUrl ? (
                  <a href={prUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                    View PR #{prUrl.match(/pull-requests\/(\d+)/)?.[1] || ''} on Bitbucket
                  </a>
                ) : (
                  <p className="text-xs text-emerald-600 mt-1">Check your Bitbucket repository for the new PR.</p>
                )}
              </div>
            )}

            {/* Findings Table */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50/50">
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={selectAll}
                        disabled={step !== 'select'}
                      />
                    </th>
                    <th className="text-left font-medium text-slate-500 px-3 py-2.5 w-24">Severity</th>
                    <th className="text-left font-medium text-slate-500 px-3 py-2.5 w-28">Type</th>
                    <th className="text-left font-medium text-slate-500 px-3 py-2.5">Finding</th>
                    <th className="text-left font-medium text-slate-500 px-3 py-2.5 w-40">Package</th>
                    <th className="text-left font-medium text-slate-500 px-3 py-2.5 w-36">Version</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map(finding => {
                    const sev = severityConfig[finding.severity] || severityConfig.info
                    const isSelected = selected.has(finding.id)
                    const hasPR = finding.finding_status === 'in_progress' && finding.fix_pr_url
                    const meta = finding.finding_metadata as any
                    // Try metadata first, fallback to parsing from title
                    let pkgName = meta?.package_name || meta?.dependency_name
                    let currentVer = meta?.current_version || meta?.installed_version
                    let latestVer = meta?.latest_version || meta?.recommended_version || meta?.fixed_version

                    // Parse from title: "Outdated: package_name X.Y.Z -> A.B.C (N major behind)"
                    if (!pkgName && finding.title) {
                      const m = finding.title.match(/^(?:Outdated|Vulnerability[^:]*): (\S+?)(?:@(\S+))? (\S+) -> (\S+)/)
                      if (m) {
                        pkgName = m[1]
                        currentVer = currentVer || m[2] || m[3]
                        latestVer = latestVer || m[4]
                      } else {
                        // CVE format: "Vulnerability in pkg@ver: CVE-..."
                        const m2 = finding.title.match(/Vulnerability in (\S+?)@(\S+)/)
                        if (m2) {
                          pkgName = m2[1]
                          currentVer = currentVer || m2[2]
                        }
                      }
                    }

                    return (
                      <tr
                        key={finding.id}
                        className={`border-b last:border-0 transition-colors ${
                          hasPR ? 'opacity-50' : 'cursor-pointer'
                        } ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50/50'}`}
                        onClick={() => !hasPR && toggleFinding(finding.id)}
                      >
                        <td className="px-3 py-2.5">
                          {hasPR ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleFinding(finding.id)}
                              disabled={step !== 'select'}
                            />
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {hasPR ? (
                            <a href={finding.fix_pr_url!} target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="inline-flex items-center gap-1 hover:opacity-80">
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 border-0 text-xs gap-1 cursor-pointer">
                                <GitPullRequest className="h-3 w-3" />
                                PR #{finding.fix_pr_url?.match(/pull-requests\/(\d+)/)?.[1] || 'Open'}
                              </Badge>
                            </a>
                          ) : (
                            <Badge variant="secondary" className={`${sev.bg} ${sev.color} border-0 text-xs`}>
                              {sev.label}
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs text-slate-600">
                            {typeLabels[finding.finding_type] || finding.finding_type}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-slate-900 truncate max-w-md">{finding.title}</p>
                              </TooltipTrigger>
                              {finding.description && (
                                <TooltipContent side="bottom" className="max-w-sm text-xs">
                                  {finding.description}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                          {finding.cve_id && (
                            <a
                              href={finding.cve_id.startsWith('GHSA-')
                                ? `https://github.com/advisories/${finding.cve_id}`
                                : `https://nvd.nist.gov/vuln/detail/${finding.cve_id}`}
                              target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="text-xs text-red-500 font-mono hover:text-red-700 hover:underline"
                            >{finding.cve_id}</a>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {pkgName && (
                            <div className="flex items-center gap-1.5">
                              <Package className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-xs font-mono text-slate-700 truncate max-w-[120px]">{pkgName}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {currentVer && latestVer ? (
                            <div className="flex items-center gap-1 text-xs font-mono">
                              <span className="text-slate-500">{currentVer}</span>
                              <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                              <span className="text-emerald-700 font-semibold">{latestVer}</span>
                            </div>
                          ) : currentVer ? (
                            <span className="text-xs font-mono text-slate-500">{currentVer}</span>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Release Pipeline Status */}
            <GuardianReleaseStatus
              appSlug={appSlug}
              appName={appName}
              hasPR={findings.some(f => f.finding_status === 'in_progress' && f.fix_pr_url)}
            />

            {/* Severity Legend */}
            <div className="grid grid-cols-5 gap-3 text-[11px] text-slate-500 pt-2 border-t mt-3">
              <div className="pt-2">
                <span className="font-semibold text-red-600 block">Critical</span>
                <span>Actively exploited CVE or security advisory</span>
              </div>
              <div className="pt-2">
                <span className="font-semibold text-orange-600 block">High</span>
                <span>Known CVE or 3+ major versions behind on security-sensitive package</span>
              </div>
              <div className="pt-2">
                <span className="font-semibold text-amber-600 block">Medium</span>
                <span>2 major versions behind or CVE with moderate impact</span>
              </div>
              <div className="pt-2">
                <span className="font-semibold text-blue-600 block">Low</span>
                <span>1 major version behind on a production dependency</span>
              </div>
              <div className="pt-2">
                <span className="font-semibold text-slate-400 block">Info</span>
                <span>Dev-only updates, patch versions, or pre-release targets</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
